import type { ReactNode } from 'react';

/**
 * Spec 233 — a section of a `.hub-page--reading` page.
 *
 * Sections open on a hairline (`.hub-section` + `.hub-section__head` in the
 * vendored layout.css) — the reading archetype's equivalent of `Region` for
 * dashboards. Like `Region`, a `Section` must never carry its own border,
 * margin or radius: the hairline is drawn by `.hub-section__head` itself, and
 * `.hub-section` only contributes the top padding between consecutive
 * sections.
 */
export function Section({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Record<string, unknown>) {
  return (
    <section className={`hub-section${className ? ' ' + className : ''}`} {...rest}>
      {children}
    </section>
  );
}

/**
 * The section head: a heading, baseline-aligned with an optional right-hand
 * slot. That slot holds something TRUE — a count, a timestamp, a status — or
 * is omitted entirely. Never a button added because the row looked bare, and
 * never a figure that is really an estimate (spec 233 §2).
 *
 * Bare `<h2>` inside — `.hub-section__head` styles its own heading, the same
 * pattern `RegionHead` uses for `.hub-region__head`.
 */
export function SectionHead({ title, right }: { title: ReactNode; right?: ReactNode }) {
  return (
    <div className="hub-section__head">
      <h2>{title}</h2>
      {right ?? null}
    </div>
  );
}
